import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';

import { FeatureFlagValue, useFeatureFlags } from './useFeatureFlags';

/** How long to wait for flags before going on without them. */
const FLAGS_WAIT_MS = 2500;

export interface MultivariateFlagResult<V extends string, T> {
  variant: V;
  value: T;
  isReady: boolean;
}

/** The variant a flag value names, or the fallback when it names none. */
export const variantOf = <V extends string>(
  values: Record<V, unknown>,
  flag: FeatureFlagValue,
  fallback: V,
): V =>
  typeof flag === 'string' && Object.hasOwn(values, flag)
    ? (flag as V)
    : fallback;

const reportFallback = (reason: string, key: string, flag?: string) =>
  Sentry.captureMessage(`flags.multivariate_${reason}`, {
    level: 'warning',
    tags: { 'flags.key': key },
    extra: { flag },
  });

/** The value for the variant a flag picks, ready once the flags load or the wait runs out. */
export const useMultivariateFlag = <V extends string, T>(
  key: string,
  values: Record<V, T>,
  fallback: V,
): MultivariateFlagResult<V, T> => {
  const { flags, ready } = useFeatureFlags();
  const [isWaitOver, setIsWaitOver] = useState(false);
  const flag = flags[key];

  useEffect(() => {
    if (ready) return;
    const timer = setTimeout(() => setIsWaitOver(true), FLAGS_WAIT_MS);
    return () => clearTimeout(timer);
  }, [ready]);

  // A value cached from an earlier visit is still routed on, so only a missing one is a fallback.
  useEffect(() => {
    if (isWaitOver && !ready && flag === undefined) {
      reportFallback('wait_timed_out', key);
    }
  }, [isWaitOver, ready, flag, key]);

  useEffect(() => {
    if (typeof flag === 'string' && !Object.hasOwn(values, flag)) {
      reportFallback('unknown_variant', key, flag);
    }
  }, [key, flag, values]);

  const variant = variantOf(values, flag, fallback);
  return { variant, value: values[variant], isReady: ready || isWaitOver };
};
